import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { MagnifyingGlass, ArrowCounterClockwise, Archive, CaretLeft, CaretRight, CaretDoubleLeft, CaretDoubleRight, DownloadSimple } from "@phosphor-icons/react";
import { SearchableSelect } from "@/components/SearchableSelect";
import { toast } from "sonner";
import { ExportColumnsDialog } from "@/components/ExportColumnsDialog";
import { useAuth } from "@/context/AuthContext";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso; }
};

export default function ArchivedMembers() {
  const { user } = useAuth();
  const canRestore = ["national_manager", "super_admin"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [reasonFilter, setReasonFilter] = useState("");
  const [regionFilter, setRegionFilter] = useState("");
  const [archiverFilter, setArchiverFilter] = useState("");
  const [filterOpts, setFilterOpts] = useState({ reasons: [], regions: [], archivers: [] });
  const [exporting, setExporting] = useState(false);
  const [pickOpen, setPickOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const PAGE_SIZE = 30;

  const params = { search, reason: reasonFilter || undefined, region_name: regionFilter || undefined, archived_by: archiverFilter || undefined };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/archived-members", { params: { ...params, page, page_size: PAGE_SIZE } });
      setItems(data.items);
      setTotal(data.total);
      setTotalPages(data.total_pages);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, reasonFilter, regionFilter, archiverFilter, page]);

  useEffect(() => { api.get("/archived-members/filter-options").then((r) => setFilterOpts(r.data)).catch(() => {}); }, []);
  useEffect(() => { setPage(1); }, [search, reasonFilter, regionFilter, archiverFilter]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const handleExport = async (pick) => {
    setExporting(true);
    try {
      const res = await api.get("/export/archived-members", { responseType: "blob", params: { ...params, ...pick } });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `archived_members_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Archived members exported");
      setPickOpen(false);
    } catch { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  const restore = async () => {
    setBusy(true);
    try {
      await api.post(`/archived-members/${restoreTarget.id}/restore`);
      toast.success(`${restoreTarget.first_name} ${restoreTarget.last_name} restored to the active directory`);
      setRestoreTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <div className="animate-fade-up">
        <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Directory</span>
        <div className="flex items-center gap-3 mt-1 justify-between flex-wrap">
          <div className="flex items-center gap-3">
            <Archive size={28} weight="bold" className="text-primary" />
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Archived Members</h1>
          </div>
          <Button onClick={() => setPickOpen(true)} disabled={exporting} variant="outline"
            data-testid="export-archived-button" className="gap-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground">
            <DownloadSimple size={18} weight="bold" /> {exporting ? "Exporting…" : "Export to Excel"}
          </Button>
        </div>
        <p className="text-sm text-muted-foreground mt-2">
          {total.toLocaleString()} archived member{total !== 1 ? "s" : ""}. Review the record, reason and who archived it{canRestore ? " — restore anyone back to the active directory" : ""}.
        </p>

        <div className="relative mt-6 max-w-md">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="archived-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, staff ID, facility, region…" className="pl-10" />
        </div>

        <div className="flex flex-col sm:flex-row flex-wrap gap-3 mt-3">
          <SearchableSelect testId="archived-reason-filter" className="w-full sm:w-52" placeholder="All reasons" allLabel="All reasons"
            value={reasonFilter} options={filterOpts.reasons} onChange={setReasonFilter} />
          <SearchableSelect testId="archived-region-filter" className="w-full sm:w-52" placeholder="All regions" allLabel="All regions"
            value={regionFilter} options={filterOpts.regions} onChange={setRegionFilter} />
          <SearchableSelect testId="archived-archiver-filter" className="w-full sm:w-52" placeholder="Archived by anyone" allLabel="Archived by anyone"
            value={archiverFilter} options={filterOpts.archivers} onChange={setArchiverFilter} />
          {(reasonFilter || regionFilter || archiverFilter) && (
            <Button variant="ghost" size="sm" data-testid="archived-clear-filters"
              onClick={() => { setReasonFilter(""); setRegionFilter(""); setArchiverFilter(""); }}>Clear filters</Button>
          )}
        </div>

        {/* Mobile cards */}
        <div className="mt-6 space-y-3 md:hidden" data-testid="archived-mobile-list">
          {loading ? (
            <Card className="p-6 text-center text-muted-foreground">Loading…</Card>
          ) : items.length === 0 ? (
            <Card className="p-6 text-center text-muted-foreground">No archived members.</Card>
          ) : items.map((m) => (
            <Card key={m.id} data-testid={`archived-card-${m.staff_id}`} className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium truncate">{[m.title, m.first_name, m.last_name].filter(Boolean).join(" ")}</p>
                  <p className="text-xs text-muted-foreground font-mono">{m.staff_id}{m.membership_id ? ` · ${m.membership_id}` : ""}</p>
                  <p className="text-xs text-muted-foreground truncate mt-0.5">{m.facility_name || "—"}</p>
                  <p className="text-xs text-muted-foreground mt-1">Archived {fmtDate(m.archived_date)}{m.archive_reason ? ` · ${m.archive_reason}` : ""}</p>
                </div>
                {canRestore && (
                  <Button variant="outline" size="sm" onClick={() => setRestoreTarget(m)}
                    data-testid={`restore-mobile-${m.staff_id}`} className="gap-1.5 shrink-0">
                    <ArrowCounterClockwise size={15} weight="bold" /> Restore
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>

        <Card className="mt-6 border-border overflow-hidden hidden md:block">
          <div className="overflow-auto max-h-[calc(100vh-340px)]">
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-card shadow-[0_1px_0_0_hsl(var(--border))]">
                <TableRow className="hover:bg-transparent">
                  <TableHead>Staff ID</TableHead>
                  <TableHead>Full Name</TableHead>
                  <TableHead className="hidden md:table-cell">Name of Facility</TableHead>
                  <TableHead className="hidden lg:table-cell">Archived On</TableHead>
                  <TableHead className="hidden lg:table-cell">Reason</TableHead>
                  {canRestore && <TableHead className="text-right">Action</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
                ) : items.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">No archived members.</TableCell></TableRow>
                ) : items.map((m) => (
                  <TableRow key={m.id} data-testid={`archived-row-${m.staff_id}`}>
                    <TableCell className="font-mono text-sm">{m.staff_id}</TableCell>
                    <TableCell>
                      <p className="font-medium">{[m.title, m.first_name, m.last_name].filter(Boolean).join(" ")}</p>
                      {m.membership_id && <p className="text-xs text-muted-foreground font-mono">{m.membership_id}</p>}
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{m.facility_name || "—"}</TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                      {fmtDate(m.archived_date)}
                      {m.archived_by_staff_id && <span className="block text-xs">by {m.archived_by_staff_id}</span>}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-muted-foreground max-w-[16rem] truncate">{m.archive_reason || "—"}</TableCell>
                    {canRestore && (
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm" onClick={() => setRestoreTarget(m)}
                          data-testid={`restore-${m.staff_id}`} className="gap-1.5">
                          <ArrowCounterClockwise size={15} weight="bold" /> Restore
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>

        {total > 0 && (
          <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground" data-testid="archived-summary">
              Showing {((page - 1) * PAGE_SIZE + 1).toLocaleString()} to {Math.min(page * PAGE_SIZE, total).toLocaleString()} of {total.toLocaleString()}
            </p>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground mr-1">Page {page} of {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(1)} data-testid="archived-first" className="gap-1"><CaretDoubleLeft size={14} weight="bold" /> First</Button>
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="archived-prev" className="gap-1"><CaretLeft size={14} weight="bold" /> Prev</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="archived-next" className="gap-1">Next <CaretRight size={14} weight="bold" /></Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(totalPages)} data-testid="archived-last" className="gap-1">Last <CaretDoubleRight size={14} weight="bold" /></Button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!restoreTarget} onOpenChange={(o) => !o && setRestoreTarget(null)}>
        <DialogContent className="max-w-md" data-testid="restore-confirm-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Restore member</DialogTitle></DialogHeader>
          {restoreTarget && (
            <>
              <p className="text-sm text-muted-foreground">
                Restore <span className="font-semibold text-foreground">{restoreTarget.first_name} {restoreTarget.last_name}</span> ({restoreTarget.staff_id}) back to the active directory?
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setRestoreTarget(null)} disabled={busy}>Cancel</Button>
                <Button onClick={restore} disabled={busy} data-testid="confirm-restore-button" className="gap-2">
                  <ArrowCounterClockwise size={16} weight="bold" /> {busy ? "Restoring…" : "Restore"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <ExportColumnsDialog open={pickOpen} onOpenChange={setPickOpen} kind="archived" onExport={handleExport} exporting={exporting} />
    </Layout>
  );
}
